import { closeRfq } from 'actions/rfqActions';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import ComparisontableTesting from './ComparisontableTesting';
import ReactQuill from 'react-quill';
import axios from "utils/axios";
import { RFQ_ATTACHMENTS_UPLOAD_URL } from 'config/services';
import { readFileAsBase64Payload } from "utils/fileUploadUtils";
import AwardJustificationModal from './AwardJustificationModal';
import { buildAwardJustificationContext, getNonL1JustificationOptions } from './awardJustificationUtils';
import { getUnquotedItemsWarning } from './quotationLineHelpers';

const CloseRfqOpt = ({ closeModal, data, rfqType, suppliersWithQuotation, isApprovalRequired, selectedSupplier, allSuppliers, rfqId, entityId, closeRfqForm, handleRfqCloseModel, selectedMaterialsMap, setSelectedMaterialsMap, chargeInputs, setChargeInputs,
  discountOnBasicAmountInputs, setDiscountOnBasicAmountInputs,
  totalSelectedQuotationValue, setTotalSelectedQuotationValue, selectedVendors,
  setSelectedVendors, showApproverComments }) => {
  const dispatch = useDispatch();
  const rfqData = useSelector((state) => state.rfq);
  const { enqueueSnackbar } = useSnackbar();
  const { loading, rfq, errorMassage } = rfqData;
  const auth = useSelector((state) => state.auth);
  const user = auth?.user;
  const isAdvancedTransport = data?.transportOption === "perUnitAmount";

  const [rejectionReason, setRejectionReason] = useState('');
  const [approverComment, setApproverComment] = useState('');
  const [approverCommentError, setApproverCommentError] = useState('');
  const [approverCommentFiles, setApproverCommentFiles] = useState([]);
  const [isUploadingApproverFiles, setIsUploadingApproverFiles] = useState(false);
  const [isDrawerClosing, setIsDrawerClosing] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [awardJustificationContext, setAwardJustificationContext] = useState(null);
  const [pendingApprovalPayload, setPendingApprovalPayload] = useState(null);
  const [isSubmittingJustification, setIsSubmittingJustification] = useState(false);
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);
  const entityUploadLimitMB = user?.userEntity?.limits?.rfqFileUploadSize;
  const maxApprovalFileSize = (entityUploadLimitMB && entityUploadLimitMB > 0 ? entityUploadLimitMB : 10) * 1024 * 1024;

  useEffect(() => {
    if (closeRfqForm) {
      setIsDrawerClosing(false);
      setIsDrawerVisible(false);
      const frameId = window.requestAnimationFrame(() => {
        setIsDrawerVisible(true);
      });
      return () => window.cancelAnimationFrame(frameId);
    }
  }, [closeRfqForm]);

  const closeDrawerWithTransition = (onClose) => {
    setIsDrawerClosing(true);
    window.setTimeout(() => {
      setIsDrawerClosing(false);
      setIsDrawerVisible(false);
      onClose?.();
    }, 360);
  };


  const additionalVendors = allSuppliers
    .filter(supplier => supplier.businessName !== selectedSupplier?.businessName)
    .map(supplier => supplier.businessName);

  const handleCheckboxChange = (vendor) => {
    setSelectedVendors((prevSelected) =>
      prevSelected.includes(vendor)
        ? prevSelected.filter((v) => v !== vendor)
        : [...prevSelected, vendor]
    );
  };

  const removeVendorChip = (vendor) => {
    // Prevent removal if it's the selected supplier
    if (vendor !== selectedSupplier?.businessName) {
      setSelectedVendors((prevSelected) => prevSelected.filter((v) => v !== vendor));
    }
  };

  const handleApproverFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const existingSize = approverCommentFiles.reduce((sum, file) => sum + (file.size || 0), 0);
    const newSize = files.reduce((sum, file) => sum + (file.size || 0), 0);

    if (existingSize + newSize > maxApprovalFileSize) {
      enqueueSnackbar(`Total attachment size must be under ${entityUploadLimitMB || 10} MB`, {
        variant: "warning",
        autoHideDuration: 4000,
      });
      event.target.value = "";
      return;
    }

    setApproverCommentFiles((prev) => [...prev, ...files]);
    event.target.value = "";
  };

  const removeApproverFile = (fileIndex) => {
    setApproverCommentFiles((prev) => prev.filter((_, index) => index !== fileIndex));
  };

  const uploadApproverCommentFiles = async () => {
    if (!approverCommentFiles.length) return [];

    const filesPayload = await Promise.all(
      approverCommentFiles.map((file) => readFileAsBase64Payload(file))
    );

    const response = await axios.post(RFQ_ATTACHMENTS_UPLOAD_URL, filesPayload);
    return Array.isArray(response?.data) ? response.data : [];
  };

  const attachApprovalFilesToPayload = async (payload) => {
    if (!showApproverComments || approverCommentFiles.length === 0) {
      return payload;
    }

    try {
      setIsUploadingApproverFiles(true);
      const approverCommentAttachments = await uploadApproverCommentFiles();
      return { ...payload, approverCommentAttachments };
    } catch (error) {
      enqueueSnackbar("Unable to upload approval attachments. Please try again.", {
        variant: "error",
        autoHideDuration: 4000,
      });
      console.error("Approval attachment upload failed:", error);
      throw error;
    } finally {
      setIsUploadingApproverFiles(false);
    }
  };

  const dispatchCloseRfq = async (basePayload) => {
    const payload = await attachApprovalFilesToPayload(basePayload);
    await dispatch(closeRfq({ payload }));
    enqueueSnackbar(basePayload?.isApprovalRequired ? "RFQ send for approval" : "RFQ closed successfully", {
      variant: 'success',
      autoHideDuration: 3000
    });
    closeModal();
  };

  const getRfqMaterialQuantity = (materialId) => {
    const material = data?.materials?.find((item) => String(item?._id) === String(materialId));
    const quantity = Number(material?.quantity || 0);
    return Number.isFinite(quantity) ? quantity : 0;
  };

  const getRfqLabourQuantity = (labourId) => {
    const labour = data?.labourDetails?.find((item) => String(item?._id) === String(labourId));
    const quantity = Number(labour?.quantity || 0);
    return Number.isFinite(quantity) ? quantity : 0;
  };

  const getContractorScopes = () =>
    (data?.providingFixingDetails || []).flatMap((section) =>
      (section?.scopes || []).map((scope) => ({
        ...scope,
        sectionId: section?._id,
        sectionName: section?.sectionName,
        isMaterialBacked: Array.isArray(scope?.materials) && scope.materials.length > 0,
      }))
    );

  const getSelectedQuantity = (material) => {
    const quantity = Number(
      material?.selectedQuantity !== undefined && material?.selectedQuantity !== null
        ? material.selectedQuantity
        : material?.labourId
          ? getRfqLabourQuantity(material?.labourId)
          : getRfqMaterialQuantity(material?.materialId)
    );
    return Number.isFinite(quantity) ? quantity : 0;
  };

  const isSameQuantity = (selectedQuantity, rfqQuantity) =>
    Math.abs(Number(selectedQuantity || 0) - Number(rfqQuantity || 0)) < 0.0001;

  const calculateSelectedLineAmount = (material) => {
    if (material?.scopeId) {
      return Number(material?.selectedSupplierPrice || 0);
    }

    if (material?.labourId) {
      if (material?.selectedQuantity === undefined || material?.selectedQuantity === null) {
        return Number(material?.selectedSupplierPrice || 0);
      }

      const selectedQuantity = getSelectedQuantity(material);
      const selectedLabourRate = Number(material?.selectedLabourRate || 0);
      return selectedLabourRate * selectedQuantity;
    }

    if (material?.selectedQuantity === undefined || material?.selectedQuantity === null) {
      return Number(material?.selectedSupplierPrice || 0);
    }

    const selectedQuantity = getSelectedQuantity(material);
    const selectedItemRate = Number(material?.selectedItemRate || 0);
    const selectedTransportCost = Number(material?.selectedTransportCost || 0);
    return (selectedItemRate * selectedQuantity) + selectedTransportCost;
  };

  const getDiscountOnBasicAmountPercent = (supplierId) => {
    const percent = Number(discountOnBasicAmountInputs?.[supplierId] || 0);
    if (!Number.isFinite(percent) || percent <= 0) return 0;
    return Math.min(100, percent);
  };

  const getPlainText = (value, fallback) => {
    const plainText = String(value || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .trim();
    return plainText || fallback;
  };

  const validateSplitQuantities = () => {
    if (rfqType === "contractor") {
      const selectedScopes = Object.values(selectedMaterialsMap || {}).flat();
      const selectedByScope = selectedScopes.reduce((acc, scope) => {
        const scopeId = scope?.scopeId;
        if (!scopeId) return acc;

        if (!acc[scopeId]) {
          acc[scopeId] = { quantity: 0, count: 0 };
        }
        acc[scopeId].quantity += Number(scope?.selectedQuantity || 0);
        acc[scopeId].count += 1;
        return acc;
      }, {});

      for (const scope of selectedScopes) {
        const selectedQuantity = Number(scope?.selectedQuantity || 0);
        if (selectedQuantity <= 0) {
          return `Quantity is mandatory for selected scope ${getPlainText(scope?.scopeOfWork, "selected scope")}.`;
        }
      }

      for (const scope of getContractorScopes()) {
        const scopeKey = String(scope?._id);
        const selected = selectedByScope[scopeKey];

        if (!selected) {
          return `Please select at least one vendor for scope ${getPlainText(scope?.scopeOfWork, "scope")}.`;
        }

        if (scope.isMaterialBacked) {
          if (selected.count !== 1) {
            return `Scope ${getPlainText(scope?.scopeOfWork, "scope")} has material details and must be awarded to one vendor only.`;
          }
          continue;
        }

        const rfqQuantity = Number(scope?.quantity || 0);
        if (selected.quantity > rfqQuantity) {
          return `Selected quantity for ${getPlainText(scope?.scopeOfWork, "scope")} is ${selected.quantity}, but RFQ quantity is ${rfqQuantity}. Please reduce the split quantity.`;
        }

        if (!isSameQuantity(selected.quantity, rfqQuantity)) {
          return `Selected quantity for ${getPlainText(scope?.scopeOfWork, "scope")} is ${selected.quantity}, but RFQ quantity is ${rfqQuantity}. Please allocate full quantity.`;
        }
      }

      return "";
    }

    if (rfqType === "labour") {
      const selectedLabours = Object.values(selectedMaterialsMap || {}).flat();
      const quantityByLabour = selectedLabours.reduce((acc, labour) => {
        const labourId = labour?.labourId;
        if (!labourId) return acc;

        acc[labourId] = (acc[labourId] || 0) + getSelectedQuantity(labour);
        return acc;
      }, {});

      for (const labour of selectedLabours) {
        const selectedQuantity = getSelectedQuantity(labour);
        if (selectedQuantity <= 0) {
          const rfqLabour = data?.labourDetails?.find((item) => String(item?._id) === String(labour?.labourId));
          return `Quantity is mandatory for selected labour ${getPlainText(rfqLabour?.skill, "selected labour")}.`;
        }
      }

      for (const labour of data?.labourDetails || []) {
        const selectedQuantity = quantityByLabour[labour?._id] || 0;
        const rfqQuantity = Number(labour?.quantity || 0);

        if (selectedQuantity > rfqQuantity) {
          return `Selected quantity for ${getPlainText(labour?.skill, "labour")} is ${selectedQuantity}, but RFQ quantity is ${rfqQuantity}. Please reduce the split quantity.`;
        }

        if (!isSameQuantity(selectedQuantity, rfqQuantity)) {
          return `Selected quantity for ${getPlainText(labour?.skill, "labour")} is ${selectedQuantity}, but RFQ quantity is ${rfqQuantity}. Please allocate full quantity.`;
        }
      }

      return "";
    }

    const selectedMaterials = Object.values(selectedMaterialsMap || {}).flat();
    const quantityByMaterial = selectedMaterials.reduce((acc, material) => {
      const materialId = material?.materialId;
      if (!materialId) return acc;

      acc[materialId] = (acc[materialId] || 0) + getSelectedQuantity(material);
      return acc;
    }, {});

    for (const material of selectedMaterials) {
      const selectedQuantity = getSelectedQuantity(material);
      if (selectedQuantity <= 0) {
        const rfqMaterial = data?.materials?.find((item) => String(item?._id) === String(material?.materialId));
        return `Quantity is mandatory for selected material ${getPlainText(rfqMaterial?.name, "selected material")}.`;
      }
    }

    for (const material of data?.materials || []) {
      const selectedQuantity = quantityByMaterial[material?._id] || 0;
      const rfqQuantity = Number(material?.quantity || 0);

      if (selectedQuantity > rfqQuantity) {
        return `Selected quantity for ${getPlainText(material?.name, "material")} is ${selectedQuantity}, but RFQ quantity is ${rfqQuantity}. Please reduce the split quantity.`;
      }

      if (!isSameQuantity(selectedQuantity, rfqQuantity)) {
        return `Selected quantity for ${getPlainText(material?.name, "material")} is ${selectedQuantity}, but RFQ quantity is ${rfqQuantity}. Please allocate full quantity.`;
      }
    }

    return "";
  };

  const calculateClosingAmounts = (selectedMaterialsMap, chargeInputs, isAdvancedTransport) => {
    return Object.entries(selectedMaterialsMap).map(([supplierId, materials]) => {
      // Sum up selectedSupplierPrice for selected materials
      const selectedMaterialBasePriceSum = materials.reduce((sum, material) => {
        return sum + calculateSelectedLineAmount(material);
      }, 0);
      const discountOnBasicAmount = rfqType === "material"
        ? selectedMaterialBasePriceSum * getDiscountOnBasicAmountPercent(supplierId) / 100
        : 0;
      const selectedMaterialAmountAfterDiscount = Math.max(0, selectedMaterialBasePriceSum - discountOnBasicAmount);

      // Get charge inputs for this supplier
      const supplierCharges = chargeInputs[supplierId] || {};

      // Calculate total extra charges, excluding transport if isAdvancedTransport is true
      const totalExtraCharges = Object.entries(supplierCharges).reduce((sum, [chargeType, chargeValue]) => {
        // console.log("chargeType", chargeType)
        if (isAdvancedTransport && chargeType.toLowerCase() === "transport") {
          return sum; // Skip transport charge
        }
        return sum + (parseFloat(chargeValue) || 0);
      }, 0);

      // Compute final closingAmount
      const closingAmount = selectedMaterialAmountAfterDiscount + totalExtraCharges;

      return { supplierId, closingAmount };
    });
  };

  const handleCloseRfq = async () => {
    if (isApprovalRequired) {
      const rateWarning = getUnquotedItemsWarning(data, suppliersWithQuotation);
      if (rateWarning) {
        enqueueSnackbar(rateWarning, { variant: "warning", autoHideDuration: 7000 });
        return;
      }
    }

    if (showApproverComments) {
      const editor = quillRef.current?.getEditor();
      const commentText = editor.getText().trim();

      if (commentText.length === 0) {
        setApproverCommentError('Comment is required');
        return;
      } else {
        setApproverCommentError('');
      }
    }

    // MATERIAL / LABOUR / CONTRACTOR VALIDATION
    if (rfqType === "material" || rfqType === "labour" || rfqType === "contractor") {
      if (rfqType === "contractor") {
        const splitQuantityError = validateSplitQuantities();
        if (splitQuantityError) {
          enqueueSnackbar(splitQuantityError, {
            variant: "warning",
            autoHideDuration: 7000
          });
          return;
        }
      } else {
      const sourceItems = rfqType === "material" ? data?.materials : data?.labourDetails;
      const itemIdKey = rfqType === "material" ? "materialId" : "labourId";
      const itemNameKey = rfqType === "material" ? "name" : "skill";
      const itemLabel = rfqType === "material" ? "material" : "labour";
      const unselectedItems = sourceItems?.filter(
        (material) =>
          !Object.values(selectedMaterialsMap || {})
            .flat()
            .some((item) => String(item?.[itemIdKey]) === String(material._id))
      );

      if (unselectedItems?.length === sourceItems?.length) {
        enqueueSnackbar("Please select rates to proceed", {
          variant: 'error',
          autoHideDuration: 4000
        });
        return;
      }

      if (unselectedItems?.length > 0) {
        const materialNames = unselectedItems?.map((m) => getPlainText(m?.[itemNameKey], itemLabel)).join(", ");
        enqueueSnackbar(
          `Please select at least one vendor for ${materialNames}`,
          { variant: "warning", autoHideDuration: 4000 }
        );
        return;
      }

      const splitQuantityError = validateSplitQuantities();
      if (splitQuantityError) {
        enqueueSnackbar(splitQuantityError, {
          variant: "warning",
          autoHideDuration: 7000
        });
        return;
      }
      }
    }

    // setLoading(true); 
    if (Object.values(selectedMaterialsMap || {}).flat().length === 0 && rfqType === "contractor") {
      enqueueSnackbar("Select at least one vendor", { variant: 'warning', autoHideDuration: 3000 });
      return; // Prevent further execution
    }
    let selectedSupplierData = [];
    let totalQuotationValue = totalSelectedQuotationValue; // Default for non-contractor RFQs

    if (rfqType === "contractor") {
      selectedSupplierData = calculateClosingAmounts(selectedMaterialsMap, chargeInputs, false)
      totalQuotationValue = selectedSupplierData.reduce((sum, supplier) => sum + supplier.closingAmount, 0);

    } else {
      selectedSupplierData = calculateClosingAmounts(selectedMaterialsMap, chargeInputs, isAdvancedTransport)
      totalQuotationValue = selectedSupplierData.reduce((sum, supplier) => sum + Number(supplier.closingAmount || 0), 0);
    }

    let requiredApprovalLevels = [];

    if (user?.userEntity?.approvals) {
      // If user.userEntity.approvals is present, process the approvals
      requiredApprovalLevels = user?.userEntity?.approvals?.filter(approval => totalQuotationValue >= approval?.threshold)?.map((approval, index) => {
        const approvers = data?.businessDivision || (entityId === "ent_36" && data?.businessDivision === "")
          ? approval.businessDivisions
            .filter(division => division?.division === data.businessDivision) // Match selected division, including explicit empty-string division.
            .flatMap(division => division?.approvers)
          : []; // If data.businessDivision is null or undefined, set approvers as empty array

        // Only include approval level if approvers array is non-empty
        if (approvers?.length > 0) {
          return {
            level: index + 1, // Levels will start from 1
            threshold: approval.threshold,
            status: "PENDING",
            approvedBy: null,
            approvedAt: null,
            approvers: approvers
          };
        }
        return null; // If approvers array is empty, return null
      }).filter(level => level !== null);
    }

    // If user.managers exists, add the manager level
    if (user?.managers?.length > 0) {
      const managerLevel = {
        level: 1,
        threshold: null,
        status: "PENDING",
        approvedBy: null,
        approvedAt: null,
        approvers: user?.managers
      };

      if (user?.userEntity?.approvals) {
        // If approvals are present, spread requiredApprovalLevels
        requiredApprovalLevels = [managerLevel, ...requiredApprovalLevels?.map((levelObj) => ({
          ...levelObj,
          level: levelObj.level + 1 // Increment all subsequent levels
        }))];
      } else {
        // If approvals are not present, just add manager level without spreading
        requiredApprovalLevels = [managerLevel];
      }
    }

    // Final approval decision fix
    if (requiredApprovalLevels.length === 0 && isApprovalRequired === true) {
      isApprovalRequired = false;
    }


    const payload = {
      rfqId: rfqId,
      rfqStatus: data?.status,
      rfqType: rfqType,
      entityId: entityId,
      supplierIds: selectedSupplierData,
      rejectionReason,
      managers: user?.managers,
      isApprovalRequired,
      selectedMaterialsMap,  // Include selected materials
      chargeInputs,  // Include charge inputs
      discountOnBasicAmountInputs,
      totalSelectedQuotationValue: totalQuotationValue,
      totalQuotationValue,
      approvalDetails: requiredApprovalLevels,
      approverComment: approverComment,
      approverCommentAttachments: []
    };

    if (isApprovalRequired) {
      const context = buildAwardJustificationContext({
        rfqData: data,
        rfqType,
        suppliersWithQuotation,
        selectedMaterialsMap,
      });

      if (context?.requiresJustification) {
        setAwardJustificationContext(context);
        setPendingApprovalPayload(payload);
        return;
      }
    }

    try {
      await dispatchCloseRfq(payload);
    } catch (error) {
      // Handle error logic
      enqueueSnackbar("Error closing RFQ", { variant: 'error', autoHideDuration: 3000 });
      console.error('Error closing RFQ:', error);
    } finally {
      // End loading state regardless of success/failure
      // setLoading(false);
    }
  };

  const handleAwardJustificationSubmit = async (selectedOptions, remark) => {
    if (!pendingApprovalPayload) return;

    try {
      setIsSubmittingJustification(true);
      await dispatchCloseRfq({
        ...pendingApprovalPayload,
        awardJustification: {
          options: selectedOptions,
          remark,
          triggerType: awardJustificationContext?.triggerType,
          stage: "RFQ_CREATOR_SELECTION",
        },
      });
      setAwardJustificationContext(null);
      setPendingApprovalPayload(null);
    } catch (error) {
      enqueueSnackbar("Error sending RFQ for approval", { variant: 'error', autoHideDuration: 3000 });
      console.error('Error sending RFQ for approval:', error);
    } finally {
      setIsSubmittingJustification(false);
    }
  };




  if (!closeRfqForm) return null;

  return (
      <div className="fixed inset-0 z-50 overflow-hidden">
        <style>
          {`
            .approval-drawer .no-padding .ql-container {
              height: 42px !important;
              padding: 0 !important;
              border: none !important;
              overflow: hidden !important;
            }

            .approval-drawer .no-padding .ql-editor {
              height: 42px !important;
              max-height: 42px !important;
              overflow: hidden !important;
              padding: 3px 6px !important;
              font-size: 0.875rem !important;
              line-height: 1.25rem !important;
              white-space: normal !important;
            }
          `}
        </style>

        <div
          className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ease-out ${isDrawerClosing || !isDrawerVisible ? "opacity-0" : "opacity-100"}`}
          onClick={() => closeDrawerWithTransition(handleRfqCloseModel)}
        />

        <section
          className={`approval-drawer absolute right-0 top-0 flex h-full w-full flex-col bg-white shadow-2xl transition-transform duration-300 ease-out ${isDrawerClosing || !isDrawerVisible ? "translate-x-full" : "translate-x-0"}`}
        >
          <div className="shrink-0 border-b border-gray-200 bg-white px-6 py-3">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="mb-2 text-lg font-semibold">
                  Select Vendor & Material Rates -
                  <span className="ml-2 rounded-sm bg-pink-200 px-1 py-1 text-[14px] font-semibold">
                    Current Selected Quotation Value: Rs. {new Intl.NumberFormat("en-IN").format(totalSelectedQuotationValue)}
                  </span>
                </div>

                {showApproverComments && (
                  <>
                    <div className="mb-1 flex items-center justify-between gap-3">
                      <p className="text-sm font-medium">Comments for Approver:</p>
                      <p className="text-xs font-normal italic text-gray-500">
                        Max 500 characters ({quillRef.current?.getEditor().getText().trim().length || 0}/500)
                      </p>
                    </div>

                    <div className="flex flex-col gap-2">
                      <div className="min-w-0 flex-1">
                        <ReactQuill
                          ref={quillRef}
                          value={approverComment}
                          onChange={(value) => {
                            const editor = quillRef.current?.getEditor();
                            const plainText = editor.getText();
                            const textLength = plainText.trim().length;

                            if (textLength <= 500) {
                              setApproverComment(value);
                            } else {
                              const truncated = plainText.trim().substring(0, 500);
                              editor.setText(truncated);
                              setApproverComment(editor.root.innerHTML);
                            }
                          }}
                          theme="snow"
                          className="no-padding h-[42px] min-h-[42px] w-full overflow-hidden border border-gray-300 p-0 text-sm font-normal word-break whitespace-normal focus:outline-none focus:ring-0"
                          modules={{ toolbar: false }}
                        />

                        {approverCommentError && (
                          <p className="mt-1 text-xs text-red-500">{approverCommentError}</p>
                        )}
                      </div>

                      <div className="flex min-h-[36px] flex-wrap items-center gap-2 rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-1.5">
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handleApproverFileSelect}
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="shrink-0 rounded-sm border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium hover:bg-gray-100"
                        >
                          Attach Files
                        </button>
                        <span className="shrink-0 text-[11px] text-gray-500">
                          Optional, max {entityUploadLimitMB || 10} MB
                        </span>

                        {approverCommentFiles.length > 0 ? (
                          <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                            {approverCommentFiles.map((file, index) => (
                              <span
                                key={`${file.name}-${index}`}
                                className="flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px]"
                              >
                                <span className="max-w-[220px] truncate">{file.name}</span>
                                <button
                                  type="button"
                                  onClick={() => removeApproverFile(index)}
                                  className="font-semibold text-red-600 hover:text-red-800"
                                >
                                  x
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[11px] text-gray-400">No files attached</span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <button
                className="flex shrink-0 items-center gap-2 px-1 text-xs text-[#3F4B64] hover:bg-slate-100"
                onClick={() => closeDrawerWithTransition(handleRfqCloseModel)}
              >
                Close
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 1L11 11M11 1L1 11"
                    stroke="#232E9B"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto border">
            <ComparisontableTesting
              rfqData={rfqData?.rfq}
              data={data}
              userProfile={user}
              suppliersWithQuotation={suppliersWithQuotation}
              closeRfqForm={closeRfqForm}
              selectedMaterialsMap={selectedMaterialsMap}
              setSelectedMaterialsMap={setSelectedMaterialsMap}
              chargeInputs={chargeInputs}
              setChargeInputs={setChargeInputs}
              discountOnBasicAmountInputs={discountOnBasicAmountInputs}
              setDiscountOnBasicAmountInputs={setDiscountOnBasicAmountInputs}
              totalSelectedQuotationValue={totalSelectedQuotationValue}
              setTotalSelectedQuotationValue={setTotalSelectedQuotationValue}
              selectedVendors={selectedVendors}
              setSelectedVendors={setSelectedVendors}
            />
          </div>

          <div
            style={{ boxShadow: '0 -4px 6px rgba(0, 0, 0, 0.1)' }}
            className="z-10 flex shrink-0 justify-end space-x-2 bg-white px-6 py-2"
          >
            <button
              onClick={() => closeDrawerWithTransition(closeModal)}
              className="border px-4 py-2 text-xs font-normal text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              disabled={isUploadingApproverFiles}
              onClick={handleCloseRfq}
              className={`bg-black px-4 py-2 text-xs font-normal text-white hover:bg-gray-800 ${isUploadingApproverFiles ? "cursor-wait opacity-60" : ""}`}
            >
              {isUploadingApproverFiles ? "Uploading..." : loading ? "Closing.." : `${isApprovalRequired ? 'Send for Approval' : 'Close RFQ'}`}
            </button>
          </div>
        </section>

        <AwardJustificationModal
          open={Boolean(awardJustificationContext)}
          options={getNonL1JustificationOptions(user?.userEntity)}
          context={awardJustificationContext}
          loading={isSubmittingJustification || isUploadingApproverFiles || loading}
          onCancel={() => {
            setAwardJustificationContext(null);
            setPendingApprovalPayload(null);
          }}
          onSubmit={handleAwardJustificationSubmit}
        />
      </div>
  );

};

export default CloseRfqOpt;
